import type {
  LoadedScript,
  ScriptCapabilities,
  ScriptCapabilityApi,
  ScriptHook,
  ScriptHost,
  ScriptInvocationContext,
  ScriptLanguage,
  ScriptLimits,
} from "@roqeet/core";
import { DEFAULT_SCRIPT_LIMITS } from "@roqeet/core";
// 타입만 import(빌드 시 erase) — 실제 모듈은 lazy import로 가져온다. 이렇게 하면
// TS가 실제 quickjs-emscripten API와의 불일치를 검증하면서도 초기 번들에서 WASM을
// 떼어낼 수 있다(결정 #4 §6 lazy-load).
import type {
  QuickJSAsyncContext,
  QuickJSContext,
  QuickJSHandle,
  QuickJSWASMModule,
} from "quickjs-emscripten";

/**
 * QuickJS-WASM 스크립트 호스트 — 결정 #4/#5(권한 기반 샌드박스, JS 단일).
 *
 * 제작자 JS는 메인 컨텍스트의 DOM/네트워크/스토리지/키에 직접 접근할 수 없고,
 * 호스트가 주입한 capability API(결정 #17)만 통해 세상과 상호작용한다. QuickJS는
 * C→WASM 격리 엔진이라 ambient authority가 0이다.
 *
 * 6s+ 보호(결정 #4 §6):
 *  - WASM은 실제 JS 스크립트가 처음 로드될 때만 lazy import(초기 번들에서 제외).
 *  - invoke마다 인터럽트 핸들러로 타임아웃을 강제(무한 루프 방지).
 *  - 런타임 메모리 캡으로 메모리 폭주 방지.
 *  - invoke마다 새 컨텍스트(상태 누수 차단, 단순·안전).
 *
 * 두 실행 경로(결정 #17):
 *  - safe 등급(getVar/setVar/getChatMessages/setResult/log/random/now): 동기 변형
 *    (sync WASM). 가볍고 빠르다 — 대부분의 스크립트가 이 경로다.
 *  - elevated(network 중개 fetch): 비동기 변형(asyncify WASM)을 lazy-load해 게스트가
 *    `await fetch(...)`를 쓸 수 있게 한다. asyncify 변형은 무거우므로 network 권한 +
 *    동의가 있는 스크립트에 한해서만 로드한다.
 */
let syncModulePromise: Promise<QuickJSWASMModule> | null = null;
type AsyncContextFactory = (opts?: { intrinsics?: unknown }) => Promise<QuickJSAsyncContext>;
let asyncFactoryPromise: Promise<AsyncContextFactory> | null = null;

/** 동기 QuickJS WASM 모듈을 lazy-load(최초 1회). safe 스크립트용. */
async function getSyncModule(): Promise<QuickJSWASMModule> {
  if (!syncModulePromise) {
    syncModulePromise = import("quickjs-emscripten").then((m) => m.getQuickJS());
  }
  return syncModulePromise;
}

/** 비동기(asyncify) 컨텍스트 팩토리를 lazy-load. network 권한 스크립트용. */
async function getAsyncContextFactory(): Promise<AsyncContextFactory> {
  if (!asyncFactoryPromise) {
    asyncFactoryPromise = import("quickjs-emscripten").then(
      (m) => m.newAsyncContext as unknown as AsyncContextFactory,
    );
  }
  return asyncFactoryPromise;
}

/** 스크립트가 비동기 capability(중개 fetch / 스토리지)를 실제로 받았는지. */
function needsAsync(api: ScriptCapabilityApi): boolean {
  return (
    typeof api.fetch === "function" ||
    typeof api.storageGet === "function" ||
    typeof api.storageSet === "function"
  );
}

export class QuickJsScriptHost implements ScriptHost {
  private readonly limits: ScriptLimits;

  constructor(limits: ScriptLimits = DEFAULT_SCRIPT_LIMITS) {
    this.limits = limits;
  }

  supports(language: ScriptLanguage): boolean {
    // 정규식은 런타임 불필요(별도 처리), Lua는 후속. 이 호스트는 JS만.
    return language === "javascript";
  }

  async load(
    def: {
      id: string;
      language: ScriptLanguage;
      source: string;
      hooks: ScriptHook[];
      capabilities: ScriptCapabilities;
      consentGranted: boolean;
    },
    api: ScriptCapabilityApi,
  ): Promise<LoadedScript> {
    if (def.language !== "javascript") {
      throw new Error(`QuickJsScriptHost는 javascript만 지원합니다(받은 값: ${def.language}).`);
    }
    const limits = this.limits;
    const isAsync = needsAsync(api);

    // network 권한 스크립트만 무거운 asyncify 변형을 끌어온다(6s+ 보호).
    const asyncFactory = isAsync ? await getAsyncContextFactory() : null;
    const syncMod = isAsync ? null : await getSyncModule();

    return {
      id: def.id,
      language: def.language,
      hooks: def.hooks,

      async invoke(
        hook: ScriptHook,
        context: ScriptInvocationContext,
      ): Promise<ScriptInvocationContext> {
        // 스크립트가 setResult로 바꾼 텍스트를 받는다. 미호출이면 원본 유지.
        let resultText = context.text ?? "";
        let resultSet = false;
        const hostHooks = {
          setResult: (t: string) => {
            resultText = t;
            resultSet = true;
          },
        };

        const deadline = Date.now() + limits.timeoutMs;

        if (asyncFactory) {
          // --- 비동기 경로(중개 fetch / 스토리지) ---
          // CPU 예산(timeoutMs)은 무한 루프 방지용이다. 네트워크/스토리지 대기 시간이
          // 이 예산을 잡아먹으면 정상 스크립트도 인터럽트되므로, 대기 시간만큼
          // deadline을 뒤로 밀어 CPU 시간만 측정한다(아래 injectAsync*).
          const deadlineRef = { value: deadline };
          const ctx = await asyncFactory();
          ctx.runtime.setMemoryLimit(limits.memoryBytes);
          ctx.runtime.setInterruptHandler(() => Date.now() > deadlineRef.value);
          try {
            injectCommonApi(ctx, api, hook, context, hostHooks);
            injectAsyncFetch(ctx, api, deadlineRef);
            injectAsyncStorage(ctx, api, deadlineRef);

            const evalResult = await ctx.evalCodeAsync(def.source);
            if (evalResult.error) {
              const err = ctx.dump(evalResult.error);
              evalResult.error.dispose();
              console.error("스크립트 실행 오류", def.id, err);
              return context;
            }
            evalResult.value.dispose();
            // 게스트가 `await fetch(...)`를 쓰면 연속 실행(setResult 등)이 마이크로
            // 태스크(pending job)로 미뤄진다. evalCodeAsync는 그것까지 구동하지
            // 않으므로 여기서 남은 job을 드레인한다(인터럽트 deadline이 무한 job을
            // 막는다). asyncify 덕에 fetch는 게스트에 동기처럼 보이므로 이 시점의
            // job들은 fetch 값이 이미 해석된 순수 연속 실행이다.
            let guard = 0;
            while (ctx.runtime.hasPendingJob() && Date.now() <= deadlineRef.value) {
              if (guard++ > 100_000) break; // 폭주 방지
              const jobs = ctx.runtime.executePendingJobs();
              if (jobs.error) {
                const err = ctx.dump(jobs.error);
                jobs.error.dispose();
                console.error("스크립트 비동기 작업 오류", def.id, err);
                break;
              }
              // 성공 값은 "실행된 job 수"(number)라 dispose 불필요.
            }
            if (resultSet) return { ...context, text: resultText };
            return context;
          } finally {
            ctx.dispose();
          }
        }

        // --- 동기 경로(safe) ---
        const runtime = syncMod!.newRuntime();
        runtime.setMemoryLimit(limits.memoryBytes);
        runtime.setInterruptHandler(() => Date.now() > deadline);
        const ctx = runtime.newContext();
        try {
          injectCommonApi(ctx, api, hook, context, hostHooks);
          const evalResult = ctx.evalCode(def.source);
          if (evalResult.error) {
            const err = ctx.dump(evalResult.error);
            evalResult.error.dispose();
            // 스크립트 에러는 격리 — 호스트/대화를 죽이지 않고 원본 유지.
            console.error("스크립트 실행 오류", def.id, err);
            return context;
          }
          evalResult.value.dispose();
          if (resultSet) return { ...context, text: resultText };
          return context;
        } finally {
          ctx.dispose();
          runtime.dispose();
        }
      },

      dispose() {
        // 컨텍스트/런타임은 invoke마다 생성·해제하므로 여기선 할 일 없음.
      },
    };
  }

  dispose(): void {
    // 모듈은 프로세스 수명 동안 캐시(재사용). 명시적 해제 불필요.
  }
}

/**
 * 게스트에 safe 등급 capability API를 주입한다(결정 #17). 동기/비동기 컨텍스트
 * 양쪽에서 동일하게 동작한다(QuickJSAsyncContext는 QuickJSContext를 상속).
 * 권한 없는 메서드는 아예 주입하지 않아 게스트에서 도달 불가능하게 한다.
 *
 * QuickJS 핸들은 수동 메모리 관리라 만든 핸들은 즉시 dispose한다(전역에 set한 뒤).
 */
function injectCommonApi(
  ctx: QuickJSContext,
  api: ScriptCapabilityApi,
  hook: ScriptHook,
  context: ScriptInvocationContext,
  hostHooks: { setResult: (t: string) => void },
): void {
  const g = ctx.global;

  /** 함수 핸들을 전역에 등록하고 핸들을 해제하는 헬퍼. */
  const defineFn = (
    name: string,
    fn: (...args: QuickJSHandle[]) => QuickJSHandle | void,
  ): void => {
    const h = ctx.newFunction(name, fn);
    ctx.setProp(g, name, h);
    h.dispose();
  };

  // context 객체(읽기 전용 스냅샷): { hook, text }.
  const ctxObj = ctx.newObject();
  const hookH = ctx.newString(hook);
  ctx.setProp(ctxObj, "hook", hookH);
  hookH.dispose();
  const textH = ctx.newString(context.text ?? "");
  ctx.setProp(ctxObj, "text", textH);
  textH.dispose();
  ctx.setProp(g, "context", ctxObj);
  ctxObj.dispose();

  // setResult(text): 호스트가 사용할 결과 텍스트 교체(safe — mutateMessages).
  defineFn("setResult", (arg) => {
    const v = arg ? ctx.dump(arg) : "";
    hostHooks.setResult(typeof v === "string" ? v : String(v ?? ""));
  });

  // log(...args): 콘솔로(외부 전송 없음).
  defineFn("log", (...args) => {
    console.log("[script]", ...args.map((a) => ctx.dump(a)));
  });

  // random()/now(): 호스트 제공(재현 가능 RNG는 api.random).
  defineFn("random", () => ctx.newNumber(api.random()));
  defineFn("now", () => ctx.newNumber(api.now()));

  // getVar/setVar(scope, key[, value]): 변수 capability(safe).
  defineFn("getVar", (scopeH, keyH) => {
    const scope = String(scopeH ? ctx.dump(scopeH) : "") as "chat" | "character" | "global";
    const key = String(keyH ? ctx.dump(keyH) : "");
    const v = api.getVar(scope, key);
    if (v == null) return ctx.undefined;
    return ctx.newString(typeof v === "string" ? v : JSON.stringify(v));
  });

  defineFn("setVar", (scopeH, keyH, valH) => {
    const scope = String(scopeH ? ctx.dump(scopeH) : "") as "chat" | "character" | "global";
    const key = String(keyH ? ctx.dump(keyH) : "");
    api.setVar(scope, key, valH ? ctx.dump(valH) : undefined);
  });

  // getChatMessages(limit): 최근 메시지 읽기(safe). JSON 문자열로 전달.
  defineFn("getChatMessages", (limitH) => {
    const limit = limitH ? Number(ctx.dump(limitH)) : undefined;
    const msgs = api.getChatMessages(limit);
    return ctx.newString(JSON.stringify(msgs));
  });
}

/**
 * 게스트에 비동기 중개 fetch를 주입한다(결정 #17, elevated). 게스트에서:
 *   const res = await fetch(url, { method, headers, body });
 *   // res = { ok: boolean, status: number, body: string }
 *
 * 실제 호출은 host의 FetchBroker(api.fetch)가 수행하며, core가 allowlist·자격증명
 * 제거·SSRF 방어를 강제한다. 거부/오류는 게스트를 죽이지 않고 res.ok=false로 전달.
 *
 * deadlineRef: CPU 인터럽트 deadline. 네트워크 대기 시간은 CPU 예산이 아니므로,
 * fetch 동안 경과한 실제 시간만큼 deadline을 뒤로 밀어 정상 스크립트가 네트워크
 * 지연 때문에 인터럽트되지 않게 한다(무한 루프 방지 자체는 유지).
 */
function injectAsyncFetch(
  ctx: QuickJSAsyncContext,
  api: ScriptCapabilityApi,
  deadlineRef: { value: number },
): void {
  const brokeredFetch = api.fetch;
  if (!brokeredFetch) return;
  const g = ctx.global;

  const fetchFn = ctx.newAsyncifiedFunction("fetch", async (urlH, initH) => {
    const url = String(urlH ? ctx.dump(urlH) : "");
    const initRaw = initH ? ctx.dump(initH) : undefined;
    const init =
      initRaw && typeof initRaw === "object"
        ? (initRaw as { method?: string; headers?: Record<string, string>; body?: string })
        : undefined;

    let ok = false;
    let status = 0;
    let body = "";
    const startedAt = Date.now();
    try {
      const res = await brokeredFetch(url, init);
      ok = res.ok;
      status = res.status;
      body = await res.text();
    } catch (e) {
      // 브로커 거부(allowlist 위반 등)나 네트워크 오류 → res.ok=false로 전달.
      body = e instanceof Error ? e.message : String(e);
    } finally {
      // 네트워크 대기 시간만큼 CPU deadline을 연장(대기는 CPU 예산이 아님).
      deadlineRef.value += Date.now() - startedAt;
    }

    // 결과 객체 핸들을 만들어 게스트에 반환(소유권 이전).
    const obj = ctx.newObject();
    ctx.setProp(obj, "ok", ok ? ctx.true : ctx.false);
    const statusH = ctx.newNumber(status);
    ctx.setProp(obj, "status", statusH);
    statusH.dispose();
    const bodyH = ctx.newString(body);
    ctx.setProp(obj, "body", bodyH);
    bodyH.dispose();
    return obj;
  });
  ctx.setProp(g, "fetch", fetchFn);
  fetchFn.dispose();
}

/**
 * 게스트에 비동기 스토리지를 주입한다(결정 #17, elevated). asyncify 덕에 게스트는
 * 비동기 호출을 **동기처럼** 쓴다(await 불필요 — 권장):
 *   var data = JSON.parse(storageGet(key) || "{}");   // JSON 문자열 또는 undefined
 *   storageSet(key, data);                            // 값은 JSON 직렬화되어 영속
 *
 * (게스트에서 `await`로 감싸면 연속 실행이 pending job으로 미뤄져 다중 호출 시
 *  재진입 오류가 날 수 있으므로, 동기 스타일을 쓴다.)
 *
 * 실제 영속은 host(api.storageGet/Set)가 스크립트 네임스페이스에 수행한다(결정 #17:
 * 스크립트는 다른 스크립트/앱 데이터에 접근 불가). 값은 JSON 문자열로 오간다.
 *
 * deadlineRef: 스토리지 I/O 대기 시간은 CPU 예산이 아니므로 경과 시간만큼 deadline을
 * 뒤로 밀어 정상 스크립트가 I/O 지연으로 인터럽트되지 않게 한다.
 */
function injectAsyncStorage(
  ctx: QuickJSAsyncContext,
  api: ScriptCapabilityApi,
  deadlineRef: { value: number },
): void {
  const get = api.storageGet;
  const set = api.storageSet;
  if (!get && !set) return;
  const g = ctx.global;

  if (get) {
    const getFn = ctx.newAsyncifiedFunction("storageGet", async (keyH) => {
      const key = String(keyH ? ctx.dump(keyH) : "");
      const startedAt = Date.now();
      let json: string | undefined;
      try {
        const v = await get(key);
        json = v === undefined ? undefined : JSON.stringify(v);
      } catch (e) {
        console.error("스크립트 storageGet 오류", e);
        json = undefined;
      } finally {
        deadlineRef.value += Date.now() - startedAt;
      }
      return json === undefined ? ctx.undefined : ctx.newString(json);
    });
    ctx.setProp(g, "storageGet", getFn);
    getFn.dispose();
  }

  if (set) {
    const setFn = ctx.newAsyncifiedFunction("storageSet", async (keyH, valH) => {
      const key = String(keyH ? ctx.dump(keyH) : "");
      const value = valH ? ctx.dump(valH) : undefined;
      const startedAt = Date.now();
      try {
        await set(key, value);
      } catch (e) {
        console.error("스크립트 storageSet 오류", e);
      } finally {
        deadlineRef.value += Date.now() - startedAt;
      }
      return ctx.undefined;
    });
    ctx.setProp(g, "storageSet", setFn);
    setFn.dispose();
  }
}
